// @mui
import { useSettingsContext } from 'src/components/settings';
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState } from 'react';
// components
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import useHeroSection from 'src/hooks/use-homepage-data';
import { useSnackbar } from 'notistack';
import { postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';

// ----------------------------------------------------------------------

export default function ConstructionIndustrySection() {
  const settings = useSettingsContext();
  const homePageData = useSelector((state) => state.website.homePage);
  const { fetchOnMount } = useHeroSection();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    // Main Section Headers
    mainTitle: "",
    mainTitleColor: "#333333",
    subtitle: "",
    subtitleColor: "#8BC34A",

    // Section Background
    backgroundColor: "#f5f9f5",

    // Industry Categories
    categories: [
      {
        id: 1,
        title: "",
        titleColor: "#333333",
        circleColor: "#8BC34A",
        circleBackground: "#ffffff",
        connectorColor: "#8BC34A",
        link: ""
      },
      {
        id: 2,
        title: "",
        titleColor: "#333333",
        circleColor: "#8BC34A",
        circleBackground: "#ffffff",
        connectorColor: "#8BC34A",
        link: ""
      },
      {
        id: 3,
        title: "",
        titleColor: "#333333",
        circleColor: "#8BC34A",
        circleBackground: "#ffffff",
        connectorColor: "#8BC34A",
        link: ""
      },
      {
        id: 4,
        title: "",
        titleColor: "#333333",
        circleColor: "#8BC34A",
        circleBackground: "#ffffff",
        connectorColor: "#8BC34A",
        link: ""
      }
    ],

    // Global Styling
    primaryGreen: "#8BC34A",
    secondaryGreen: "#4CAF50",
    lightGreen: "#E8F5E8"
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const updateCategory = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((category, i) =>
        i === index ? { ...category, [field]: value } : category
      )
    }));
  };

  const addCategory = () => {
    const newId = Math.max(...formData.categories.map(c => c.id)) + 1;
    setFormData(prev => ({
      ...prev,
      categories: [...prev.categories, {
        id: newId,
        title: "New Category",
        titleColor: "#333333",
        circleColor: "#8BC34A",
        circleBackground: "#ffffff",
        connectorColor: "#8BC34A",
        link: "/new-category"
      }]
    }));
  };

  const removeCategory = (index) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index)
    }));
  };

   useEffect(() => {
      fetchOnMount();
    }, []);

    useEffect(() => {
      if (homePageData?.construction_industry_section) {
        const ConstructionIndustrySection = JSON.parse(JSON.stringify(homePageData.construction_industry_section));
        setFormData(prev => ({
          ...prev,
          ...ConstructionIndustrySection
        }));
      }
    }, [homePageData])

  // const handleSave = (isDraft = false) => {
  //   console.log(isDraft ? 'Saving as draft...' : 'Publishing...', formData);
  //   // You can implement the API call here
  // };

   const handleSave = async () => {
      setLoading(true);
      try {
        // Create a FormData object for file uploads
        const formDataToSend = new FormData();

        // Append all form data
        Object.entries(formData).forEach(([key, value]) => {
          // Skip heroImage as we'll handle it separately
          if (key !== 'heroImage') {
            // Handle nested objects like primaryButton and secondaryButton
            if (typeof value === 'object' && value !== null && !(value instanceof File)) {
              formDataToSend.append(key, JSON.stringify(value));
            } else {
              formDataToSend.append(key, value);
            }
          }
        });

        // Append the hero image if it exists
        if (formData.heroImage) {
          formDataToSend.append('heroImage', formData.heroImage);
        }

        const response = await postRequestForWebsite(
          endpoints.admin_dashboard.website.home_page.hero_section,
          // formDataToSend,
          // dataForHeroSection,
          {
            attributes: {
              ...JSON.parse(JSON.stringify(homePageData)),
              construction_industry_section: JSON.parse(JSON.stringify(formData))
            }
          },
          true
        );

        if (response.metadata?.success) {
          enqueueSnackbar({
            variant: 'success',
            message: response.metadata.message,
          });
        } else {
          handleErrorMessages(response.message || 'An error occurred.');
        }
      } catch (err) {
        console.error('Error:', err);
        handleErrorMessages(err.message || 'Submission failed.');
      } finally {
        setLoading(false);
      }
    };

  const ColorPreview = ({ color, size = 24 }) => (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '4px',
        backgroundColor: color,
        border: '1px solid #ddd',
        display: 'inline-block',
        mr: 1
      }}
    />
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Construction Industry Section
        </Typography>

        {/* Section Headers */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Section Headers</Typography>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Main Title</Typography>
            <TextField
              fullWidth
              value={formData.mainTitle}
              onChange={(e) => updateFormData('mainTitle', e.target.value)}
              placeholder="Enter main title"
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Title Color:</Typography>
              <ColorPreview color={formData.mainTitleColor} />
              <TextField
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ width: 100 }}
              />
            </Stack>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Subtitle Description</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={formData.subtitle}
              onChange={(e) => updateFormData('subtitle', e.target.value)}
              placeholder="Enter subtitle description"
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Subtitle Color:</Typography>
              <ColorPreview color={formData.subtitleColor} />
              <TextField
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ width: 100 }}
              />
            </Stack>
          </Box>
        </Box>



        {/* Categories Management */}
        <Box sx={{ mb: 2, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Industry Categories</Typography>
          </Stack>

          {formData.categories.map((category, index) => (
            <Paper key={category.id} variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'background.neutral' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Category #{category.id} - {category.title}
                </Typography>
                {/* <Button
                  color="error"
                  variant="contained"
                  size="small"
                  startIcon={<Iconify icon="mingcute:close-line" />}
                  onClick={() => removeCategory(index)}
                >
                  Remove
                </Button> */}
              </Stack>

              <Grid container spacing={3}>
                {/* Left Column - Content */}
                <Grid item xs={12} lg={6}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Category Title"
                      value={category.title}
                      onChange={(e) => updateCategory(index, 'title', e.target.value)}
                      placeholder="Enter category title"
                    />

                    <TextField
                      fullWidth
                      label="Category Link"
                      value={category.link}
                      onChange={(e) => updateCategory(index, 'link', e.target.value)}
                      placeholder="Enter category link (e.g., /challenges)"
                    />
                  </Stack>
                </Grid>

                {/* Right Column - Styling */}
                <Grid item xs={12} lg={6}>
                  <Stack spacing={3}>
                    <Typography variant="subtitle2">Visual Styling</Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Title Color</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={category.titleColor} />
                          <TextField
                            type="color"
                            value={category.titleColor}
                            onChange={(e) => updateCategory(index, 'titleColor', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Circle Color</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={category.circleColor} />
                          <TextField
                            type="color"
                            value={category.circleColor}
                            onChange={(e) => updateCategory(index, 'circleColor', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Circle Background</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={category.circleBackground} />
                          <TextField
                            type="color"
                            value={category.circleBackground}
                            onChange={(e) => updateCategory(index, 'circleBackground', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Connector Line</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={category.connectorColor} />
                          <TextField
                            type="color"
                            value={category.connectorColor}
                            onChange={(e) => updateCategory(index, 'connectorColor', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))}

          {/* <Button
            color="success"
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={addCategory}
            sx={{ mt: 2 }}
          >
            Add New Category
          </Button> */}
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 3 }}>
          {/* <Button
            variant="outlined"
            color="inherit"
            onClick={() => handleSave(true)}
          >
            Save as Draft
          </Button> */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="eva:save-fill" />}
            onClick={() => handleSave(false)}
          >
            Update
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
